/*
  Maps floodlight data to objects that can be used to display graphs in right positions
  {
    category: 'journal',
    feature: 'daily_mood.mood',
    renderProps: {
      yLabel={localeMessage('floodlight.labels.mood')}
      featureName={localeMessage('floodlight.features.mood')}
      featureIcon={featureMood}
      data={data.groups['daily_mood.mood'].map(moodDataMapper)}
      labels={['VP', 'P', 'A', 'G', 'VG']}
      headerColor={headerColors.journal}
    }
  }
*/

import { values, identity, times, is } from 'ramda';
import { headerColors } from './config';

import moodIcon from './assets/feature-mood.svg';
import symptomsIcon from './assets/feature-symptoms.svg';
import symbolsIcon from './assets/feature-symbols.svg';
import tomatoIcon from './assets/feature-pinch.svg';
import drawIcon from './assets/feature-drawing.svg';

const roundDecimal = d => Math.round((Number(d) + Number.EPSILON) * 10) / 10;

const labelsFromData = (n, values, floating = false) => {
  const max = Math.max(...values);
  const step = floating ? Number(max / n) : Math.floor(max / n);
  const labels = [...times(t => t * step, n), max];
  return floating ? labels.map(l => roundDecimal(l)) : labels;
};

const makeLabelMapper = (floating = false, n = 5) => {
  return values => labelsFromData(n, values, floating);
};

const decimalMapper = d => ({ ...d, value: !isNaN(d.value) ? roundDecimal(d.value) : 0 });

const makeFeatureMapper =
  (category, feature, icon, mapper = null, labels = null, type = null) =>
  (localeMessage, data) => ({
    yLabel: localeMessage(`floodlight.labels.${feature}`),
    featureName: localeMessage(`floodlight.features.${feature}`),
    featureIcon: icon,
    headerColor: headerColors[category],
    data: mapper ? data.map(mapper) : data,
    description: localeMessage(
      `floodlight.${window.__ROLE__ === 'PATIENT' ? 'patientDescriptions' : 'descriptions'}.${feature}`,
    ),
    localeMessage,
    labels: is(Function, labels) ? labels(data.map(d => d.value)) : labels,
    type,
  });

const featureMap = {};

const moodMap = {
  1: 'VP',
  2: 'P',
  3: 'A',
  4: 'G',
  5: 'VG',
};

const symptoms = ['B&B', 'W', 'LE', 'H&A', 'M'];

const moodDataMapper = d => ({ date: new Date(d.date), value: moodMap[d.value] });

featureMap['daily_mood.mood'] = makeFeatureMapper('journal', 'mood', moodIcon, moodDataMapper, values(moodMap));
featureMap['daily_mood.physical_condition'] = makeFeatureMapper(
  'journal',
  'physical',
  moodIcon,
  moodDataMapper,
  values(moodMap),
);

featureMap['symptoms_tracker.overview'] = makeFeatureMapper(
  'journal',
  'symptoms',
  symptomsIcon,
  identity,
  symptoms,
  'categories',
);

featureMap['ips.total_score_ips'] = makeFeatureMapper('cognition', 'symbols', symbolsIcon, identity, makeLabelMapper());
featureMap['pinch_tomatoes.tomatoes_squeezed_count.L'] = makeFeatureMapper(
  'hands',
  'tomatoes_left',
  tomatoIcon,
  identity,
  makeLabelMapper(),
);

featureMap['pinch_tomatoes.tomatoes_squeezed_count.R'] = makeFeatureMapper(
  'hands',
  'tomatoes_right',
  tomatoIcon,
  identity,
  makeLabelMapper(),
);

featureMap['draw_shape.celerity_shape_attempt.L'] = makeFeatureMapper(
  'hands',
  'celerity_left',
  drawIcon,
  decimalMapper,
  makeLabelMapper(true),
);

featureMap['draw_shape.celerity_shape_attempt.R'] = makeFeatureMapper(
  'hands',
  'celerity_right',
  drawIcon,
  decimalMapper,
  makeLabelMapper(true),
);

featureMap['u_turn.median_turn_speed'] = makeFeatureMapper(
  'walking',
  'u-turn',
  drawIcon,
  decimalMapper,
  makeLabelMapper(true),
);

const mwtLabels = [0, 20, 40, 60, 80, 100];

featureMap['2mwt.step_intensity'] = makeFeatureMapper('walking', 'step_intensity', drawIcon, identity, mwtLabels);

const mapDataGroups = (localeMessage, groups) => {
  const dataMap = {};
  Object.keys(groups).forEach(v => {
    dataMap[v] = featureMap[v] ? featureMap[v](localeMessage, groups[v]) : null;
  });
  return dataMap;
};

export default mapDataGroups;
